/**
 * @file 听牌判定（evaluateTing）。
 *
 * 查大叫（§6）与 UI 听牌提示共用同一入口，口径唯一。
 * 复用 `win.ts` 的 27 维计数向量与 `getWaits`，并以 `PlayerState.voidSuit`
 * 作为缺门约束：持有定缺花色者不能听牌。
 *
 * 平台无关、纯函数，不引用任何 DOM / Node / Workers 全局。
 */

import type { GameState, SeatIndex, TileKind, Rank } from './types';
import { kindIndexFromTileId, tileKindFromSuitRank } from './tiles';
import { getWaits } from './win';
import { InvariantViolationError } from './errors';

/**
 * 判定某座位的听牌信息与听口。
 *
 * - 口径唯一：查大叫与 UI 听牌提示都走这里，复用 `win.ts#getWaits`。
 * - 缺门约束：`voidSuit` 非空且手牌仍含该花色牌，则恒为未听（持有定缺花色者不能胡）。
 * - 张数守卫：手牌 + 副露实体张数必须 ∈ {13+gangs, 14+gangs}，否则抛
 *   `InvariantViolationError`（T-05：meldCount=2 却持 9 张 → 必须 assert 失败，
 *   而非静默返回 false），因为实体守恒一旦被打破，后续结算的零和恒等式将失去意义。
 *   ★ 杠修正：明/暗/巴杠都含 4 张实体（比碰多 1 张），每杠使「手牌+副露」总数 +1
 *   —— 正常态为 13+gangs（出牌后）或 14+gangs（摸牌后），gangs = 非 peng 副露数。
 */
export function evaluateTing(
  state: GameState,
  seat: SeatIndex,
): { readonly isTing: boolean; readonly candidates: readonly TileKind[] } {
  const p = state.players[seat];

  // 张数守卫：手牌 + 副露实体张数（副露每张牌都计入守恒）。
  // ★ 杠修正：明/暗/巴杠每副含 4 张实体（比碰多 1），故合法总数为 13+gangs 或 14+gangs。
  let tileCount = p.hand.length;
  let gangCount = 0;
  for (const m of p.melds) {
    tileCount += m.tiles.length;
    if (m.type !== 'peng') gangCount++;
  }
  const lo = 13 + gangCount;
  const hi = 14 + gangCount;
  if (tileCount !== lo && tileCount !== hi) {
    throw new InvariantViolationError(
      `evaluateTing: 座位 ${seat} 的牌张总数非法 (${tileCount})，应为 ${lo} 或 ${hi}`,
      { seat, tileCount, gangCount },
    );
  }

  // 27 维计数向量（仅含暗手牌；副露已在 meldCount 中体现）
  const counts = new Uint8Array(27);
  for (const id of p.hand) {
    const idx = kindIndexFromTileId(id);
    counts[idx] = (counts[idx] ?? 0) + 1;
  }
  const meldCount = p.melds.length;
  const voidSuit = p.voidSuit;

  const waits = getWaits(counts, meldCount, voidSuit);
  const candidates: TileKind[] = waits.map((k) =>
    tileKindFromSuitRank(Math.floor(k / 9), ((k % 9) + 1) as Rank),
  );

  return { isTing: candidates.length > 0, candidates };
}
